"use client";

import { useEffect } from "react";

const GA_ID = process.env.NEXT_PUBLIC_GA_ID ?? "G-SQTW6SPGZB";
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID ?? "GTM-TLQ6FTZ4";

/**
 * Deferred analytics loader.
 *
 * Uses requestIdleCallback + setTimeout(3000-5000ms) to ensure analytics
 * scripts never block First Contentful Paint or Largest Contentful Paint.
 * This outperforms next/script strategy="lazyOnload" alone because
 * lazyOnload still fires on window.load which can be early on fast
 * connections.
 *
 * Supports:
 * - Google Tag Manager (GTM) — preferred, manages GA4 + other tags
 * - Google Analytics 4 (GA4) — standalone fallback if no GTM
 * - Both can run simultaneously (GTM takes priority for tag management)
 */
export default function Analytics() {
  useEffect(() => {
    if (!GA_ID && !GTM_ID) return;

    const loadAnalytics = () => {
      // Initialize dataLayer before any scripts
      window.dataLayer = window.dataLayer || [];

      // Google Tag Manager — preferred method
      if (GTM_ID) {
        // Push initial GTM config
        window.dataLayer.push({
          "gtm.start": new Date().getTime(),
          event: "gtm.js",
        });

        const gtmScript = document.createElement("script");
        gtmScript.async = true;
        gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
        document.head.appendChild(gtmScript);

        // GTM noscript iframe for body (fallback for no-JS)
        const noscript = document.createElement("noscript");
        const iframe = document.createElement("iframe");
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
        iframe.height = "0";
        iframe.width = "0";
        iframe.style.display = "none";
        iframe.style.visibility = "hidden";
        noscript.appendChild(iframe);
        document.body.insertBefore(noscript, document.body.firstChild);
      }

      // Google Analytics 4 (gtag.js) — standalone if no GTM
      // If GTM is present, GA4 should be configured inside GTM instead
      if (GA_ID && !GTM_ID) {
        const gaScript = document.createElement("script");
        gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
        gaScript.async = true;
        document.head.appendChild(gaScript);

        gaScript.onload = () => {
          function gtag(...args: unknown[]) {
            window.dataLayer.push(args);
          }
          gtag("js", new Date());
          gtag("config", GA_ID, {
            send_page_view: true,
            cookie_flags: "SameSite=None;Secure",
          });
        };
      }

      // If both GA_ID and GTM_ID are set, GTM handles GA4.
      // Push GA_ID to dataLayer so GTM can reference it.
      if (GA_ID && GTM_ID) {
        window.dataLayer.push({
          event: "ga4_config",
          ga_measurement_id: GA_ID,
        });
      }
    };

    // Load on first user interaction OR after 7s timeout (whichever first).
    // Keeps PSI/Lighthouse audit window clean of GTM bytes.
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      cleanup();
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(loadAnalytics, { timeout: 5000 });
      } else {
        loadAnalytics();
      }
    };
    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "scroll", "touchstart"];
    const opts = { once: true, passive: true } as AddEventListenerOptions;
    events.forEach((e) => window.addEventListener(e, fire, opts));
    const t = window.setTimeout(fire, 7000);
    const cleanup = () => {
      window.clearTimeout(t);
      events.forEach((e) => window.removeEventListener(e, fire));
    };
    return cleanup;
  }, []);

  return null;
}

// Type augmentation for window.dataLayer
declare global {
  interface Window {
    dataLayer: unknown[];
  }
}
